/**
 * Shrinking a photograph before it is uploaded.
 *
 * A phone camera produces 3-5MB per shot, and what is being photographed here is a till
 * receipt: a strip of thermal paper whose only job is to stay legible enough to read a number
 * and a total off. Sending the original spends a clinic's upload budget on grain.
 *
 * 1600px on the long edge at JPEG quality 0.82 lands around 300KB and keeps small print
 * readable. The server accepts up to 5MB regardless, so this is a courtesy rather than a
 * gate -- and it is why the multipart limits did not have to go up to phone-photo size.
 */

const MAX_EDGE = 1600;

const JPEG_QUALITY = 0.82;

const JPEG = 'image/jpeg';

/**
 * Returns a downscaled JPEG, or the original file when there is nothing to gain.
 *
 * **Never rejects.** Canvas encoding can fail for reasons that have nothing to do with the
 * picture -- a browser that will not decode the format, a tainted canvas, memory pressure on
 * an old phone -- and none of them are a reason to stop somebody invoicing. Every failure path
 * falls back to the original file, which the server accepts anyway.
 */
export const downscaleImage = async (file: File): Promise<File> => {
    if (!file.type.startsWith('image/')) {
        return file;
    }

    try {
        const bitmap: HTMLImageElement = await loadImage(file);
        const scale: number = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));

        // Already small enough. Re-encoding it would cost quality for no size saved.
        if (scale === 1 && file.type === JPEG) {
            return file;
        }

        const canvas: HTMLCanvasElement = document.createElement('canvas');

        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);

        const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

        if (!context) {
            return file;
        }

        context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

        const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob(resolve, JPEG, JPEG_QUALITY)
        );

        if (!blob || blob.size >= file.size) {
            return file;
        }

        return new File([blob], renameToJpeg(file.name), { type: JPEG });
    } catch {
        return file;
    }
};

/** Long enough for a big photograph on a slow phone, short enough not to hold state forever. */
const DECODE_TIMEOUT_MS = 10_000;

/**
 * `createImageBitmap` would be tidier, but jsdom has neither it nor a layout engine, and this
 * path has to at least not throw under the test suite.
 *
 * <p>The timeout is not paranoia. jsdom fires neither `onload` nor `onerror`, and a browser
 * refusing a format can do the same -- without it the promise never settles, the image is
 * never collected, and anything awaiting it waits for the life of the tab.
 *
 * <p>Exported for `imageCropHelper`, which needs exactly these two properties and would
 * otherwise copy them -- and a decode timeout that exists twice is one that gets tuned once.
 *
 * <p>**The object URL is revoked before the promise resolves.** Drawing the resolved element
 * to a canvas is fine, but its `src` is dead by the time a caller holds it, so it cannot be
 * handed to an `<img>` for display. A caller that wants to *show* the file creates its own
 * URL and owns it.
 */
export const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url: string = URL.createObjectURL(file);
        const image: HTMLImageElement = new Image();

        const settle = (finish: () => void): void => {
            window.clearTimeout(timer);
            URL.revokeObjectURL(url);
            finish();
        };

        const timer: number = window.setTimeout(
            () => settle(() => reject(new Error('The image took too long to decode.'))),
            DECODE_TIMEOUT_MS
        );

        image.onload = () => settle(() => resolve(image));
        image.onerror = () => settle(() => reject(new Error('The image could not be read.')));

        image.src = url;
    });

/** The bytes are a JPEG now whatever they were before, so the name should not lie. */
const renameToJpeg = (name: string): string => `${name.replace(/\.[^.]+$/, '')}.jpg`;
